import axios from "axios";

import {AccessTokenDecoded, AuthRequest, AuthResponse} from "@/types/auth";
import {jwtDecode} from "jwt-decode";

const client_id = process.env.VUE_APP_CLIENT_ID as string;
const client_secret = process.env.VUE_APP_CLIENT_SECRET as string;
const auth_url = process.env.VUE_APP_AUTH_URL as string;
const admin_access_roles: string[] = process.env.VUE_APP_ADMIN_ACCESS_ROLES?.split(',') || [] as string[];
const admin_read_access_roles: string[] = process.env.VUE_APP_ADMIN_READ_ACCESS_ROLES?.split(',') || [] as string[];
const manager_access_roles: string[] = process.env.VUE_APP_MANAGER_ACCESS_ROLES?.split(',') || [] as string[];
const accountant_access_roles: string[] = process.env.VUE_APP_ACCOUNTANT_ACCESS_ROLES?.split(',') || [] as string[];
const university_manager_access_roles: string[] = process.env.VUE_APP_UNIVERSITY_MANAGER_ACCESS_ROLES?.split(',') || [] as string[];
const university_rector_access_roles: string[] = process.env.VUE_APP_UNIVERSITY_RECTOR_ACCESS_ROLES?.split(',') || [] as string[];
const audit_access_roles: string[] = process.env.VUE_APP_AUDIT_ACCESS_ROLES?.split(',') || [] as string[];
const access_roles: string[] = [...admin_access_roles, ...admin_read_access_roles, ...manager_access_roles, ...accountant_access_roles, ...university_manager_access_roles, ...university_rector_access_roles, ...audit_access_roles];

const api = axios.create({
    baseURL: auth_url,
    headers: {"Content-Type": "application/x-www-form-urlencoded"},
});

async function login(requestData: AuthRequest): Promise<AuthResponse> {
    const params = new URLSearchParams();
    params.append("username", requestData.username);
    params.append("password", requestData.password);
    params.append("client_id", client_id);
    params.append("client_secret", client_secret);
    params.append("grant_type", "password");

    const response = await api.post<AuthResponse>(auth_url, params);
    if (response.data.access_token && checkRolesFromToken(response.data.access_token)) {
        saveTokens(response.data, requestData.username);
        return response.data;
    }
    return {
        access_token: null,
        refresh_token: "",
        username: ""
    };
}

function checkRoleAccessFromToken(access_token: string, roles: string[]): boolean {
    if (!access_token) return false;
    const decoded: AccessTokenDecoded = jwtDecode(access_token);
    console.log('checkRoleAccessFromToken', roles)
    return roles.some(role => decoded.realm_access.roles.includes(role));
}

function checkRolesFromToken(access_token: string): boolean {
    const decoded: AccessTokenDecoded = jwtDecode(access_token);
    return access_roles.some(role => decoded.realm_access.roles.includes(role));
}

async function refreshToken(): Promise<AuthResponse> {
    const refresh_token = localStorage.getItem("refresh_token");
    const username = localStorage.getItem("username");
    const params = new URLSearchParams();
    params.append("refresh_token", refresh_token || "");
    params.append("client_id", client_id);
    params.append("client_secret", client_secret);
    params.append("grant_type", "refresh_token");
    params.append("username", username || "");

    try {
        const response = await api.post<AuthResponse>(auth_url, params);
        if (response.data.access_token) {
            saveTokens(response.data, username || "");
        }
        return response.data;
    } catch (error) {
        console.error("Failed to refresh token", error);
        throw error;
    }
}

function decode(token: string): AccessTokenDecoded {
    return jwtDecode(token);
}

function saveTokens(data: AuthResponse, username: string): void {
    localStorage.setItem("access_token", data.access_token || "");
    localStorage.setItem("refresh_token", data.refresh_token);
    localStorage.setItem("username", username);

    const decoded: AccessTokenDecoded = decode(data.access_token || "");
    if (decoded && decoded.name) {
        localStorage.setItem("username", decoded.name);
    }
}

function logout(): void {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("username");
    localStorage.removeItem("currentUserUniversityInfo");
}

function hasAccess(requiredRoles: string[]): boolean {
    const token = localStorage.getItem('access_token');
    if (!token) {
        return false;
    }
    return checkRoleAccessFromToken(token, requiredRoles);
}

function getAccessToken(): string | null {
    return localStorage.getItem("access_token");
}

function getAdminRoles(): string[] {
    return admin_access_roles || [];
}

function getAdminReadOnlyRoles(): string[] {
    return admin_read_access_roles || [];
}

function getManagerRoles(): string[] {
    return manager_access_roles || [];
}

function getAccountantRoles(): string[] {
    return accountant_access_roles || [];
}

function getUniversityManagerRoles(): string[] {
    return university_manager_access_roles || [];
}

function getUniversityRectorRoles(): string[] {
    return university_rector_access_roles || [];
}

function getAuditRoles(): string[] {
    return audit_access_roles || [];
}

export default {
    login,
    hasAccess,
    decode,
    checkRoleAccessFromToken,
    refreshToken,
    getAccessToken,
    logout,
    getAdminRoles,
    getAdminReadOnlyRoles,
    getManagerRoles,
    getAccountantRoles,
    getUniversityManagerRoles,
    getUniversityRectorRoles,
    getAuditRoles
};
